import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  Link as MuiLink,
  TextField,
  Button,
  Divider,
  Stack,
  Chip
} from '@mui/material';
import { LinkedIn, WhatsApp, Twitter } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import brandIcon from '../assets/alamen-icon.png';
import { tArray } from '../lib/i18nSafe';

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');

  const handleNewsletterSignup = () => {
    if (!email) return;
    const mailto = `mailto:ali@al-amentech.io?subject=${encodeURIComponent(
      'Newsletter subscription'
    )}&body=${encodeURIComponent(`Please add this email to the newsletter list:\n\n${email}\n`)}`;
    window.open(mailto, '_blank', 'noopener,noreferrer');
    setEmail('');
  };

  const columns = [
    {
      title: t('footer.columns.product'),
      links: [
        { label: t('nav.platform'), to: '/platform' },
        { label: t('nav.models'), to: '/models' },
        { label: t('nav.playground'), to: '/playground' },
        { label: t('nav.changelog'), to: '/changelog' }
      ]
    },
    {
      title: t('footer.columns.capabilities'),
      links: [
        { label: t('nav.architecture'), to: '/architecture' },
        { label: t('home.platform.items.0.product'), to: '/platform#arabic' },
        { label: t('home.platform.items.1.product'), to: '/platform#vision' }
      ]
    },
    {
      title: t('footer.columns.sectors'),
      links: [
        { label: t('sectorsData.government.title'), to: '/sectors#government' },
        { label: t('sectorsData.justice.title'), to: '/sectors#justice' },
        { label: t('sectorsData.healthcare.title'), to: '/sectors#healthcare' },
        { label: t('sectorsData.finance.title'), to: '/sectors#finance' },
        { label: t('sectorsData.logistics.title'), to: '/sectors#logistics' },
        { label: t('sectorsData.energy.title'), to: '/sectors#energy' }
      ]
    },
    {
      title: t('footer.columns.research'),
      links: [
        { label: t('nav.research'), to: '/blog' },
        { label: t('nav.media'), to: '/media' }
      ]
    },
    {
      title: t('footer.columns.company'),
      links: [
        { label: t('nav.about'), to: '/about' },
        { label: t('nav.contact'), to: '/contact' },
        { label: t('footer.links.trust'), to: '/trust' }
      ]
    }
  ];

  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderTop: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
        <Grid container spacing={{ xs: 5, md: 6 }}>
          <Grid item xs={12} md={3}>
            <Box
              component="img"
              src={brandIcon}
              alt={t('footer.companyName')}
              sx={{ height: 40, width: 'auto', display: 'block', mb: 1.5 }}
            />
            <Typography
              sx={{
                fontFamily: 'var(--font-serif)',
                fontSize: '2rem',
                letterSpacing: '-0.02em',
                lineHeight: 1
              }}
            >
              {t('footer.companyName')}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'var(--font-mono)',
                fontSize: '0.72rem',
                textTransform: 'uppercase',
                letterSpacing: '0.14em',
                color: 'text.secondary',
                mt: 1
              }}
            >
              {t('brand.tagline')}
            </Typography>
            <Typography sx={{ mt: 2, color: 'text.secondary', maxWidth: 360 }}>
              {t('footer.companyDesc')}
            </Typography>

            {/* Trust badges */}
            <Stack direction="row" spacing={1} sx={{ mt: 3, flexWrap: 'wrap', gap: 1 }}>
              {tArray(t, 'footer.trustBadges').map((badge) => (
                <Chip
                  key={badge}
                  label={badge}
                  variant="outlined"
                  size="small"
                  sx={{ borderColor: 'divider' }}
                />
              ))}
            </Stack>

            {/* Social */}
            <Stack direction="row" spacing={0.5} sx={{ mt: 3 }}>
              <IconButton
                size="small"
                sx={{ color: 'text.secondary' }}
                href="https://wa.me/+971543282815"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
              >
                <WhatsApp fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                sx={{ color: 'text.secondary' }}
                href="https://x.com/al_amen25895"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X"
              >
                <Twitter fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                sx={{ color: 'text.secondary' }}
                href="https://www.linkedin.com/company/al-amen-technology"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <LinkedIn fontSize="small" />
              </IconButton>
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  sm: 'repeat(3, minmax(0, 1fr))',
                  md: 'repeat(5, minmax(0, 1fr))'
                },
                gap: { xs: 3, md: 2 }
              }}
            >
              {columns.map((col) => (
                <Box key={col.title}>
                  <Typography
                    sx={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.72rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.14em',
                      color: 'text.secondary',
                      mb: 2
                    }}
                  >
                    {col.title}
                  </Typography>
                  <Stack spacing={1.1}>
                    {col.links.map((link) => (
                      <MuiLink
                        key={link.to + link.label}
                        component={RouterLink}
                        to={link.to}
                        underline="none"
                        sx={{
                          fontSize: '0.92rem',
                          color: 'text.primary',
                          '&:hover': { color: 'primary.main' }
                        }}
                      >
                        {link.label}
                      </MuiLink>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography
              sx={{
                fontFamily: 'var(--font-mono)',
                fontSize: '0.72rem',
                textTransform: 'uppercase',
                letterSpacing: '0.14em',
                color: 'text.secondary',
                mb: 2
              }}
            >
              {t('footer.newsletter')}
            </Typography>
            <Typography sx={{ color: 'text.secondary', mb: 2 }}>
              {t('footer.newsletterDesc')}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                size="small"
                placeholder={t('footer.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                InputProps={{
                  sx: {
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.88rem'
                  }
                }}
              />
              <Button
                variant="contained"
                sx={{
                  whiteSpace: 'nowrap',
                  bgcolor: 'text.primary',
                  color: 'background.default',
                  '&:hover': { bgcolor: 'text.primary', opacity: 0.9 }
                }}
                onClick={handleNewsletterSignup}
              >
                {t('footer.subscribe')}
              </Button>
            </Stack>
            <Typography
              variant="caption"
              sx={{
                mt: 1,
                display: 'block',
                color: 'text.secondary'
              }}
            >
              {t('footer.newsletterHelper')}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 5 }} />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            © {new Date().getFullYear()} Al Amen Technology · {t('footer.builtAt')}
          </Typography>
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <MuiLink
              component={RouterLink}
              to="/trust"
              underline="none"
              variant="caption"
              sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
            >
              {t('footer.links.trust')}
            </MuiLink>
            <MuiLink
              href="/llms.txt"
              underline="none"
              variant="caption"
              sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
            >
              llms.txt
            </MuiLink>
            <MuiLink
              href="/ai.txt"
              underline="none"
              variant="caption"
              sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
            >
              ai.txt
            </MuiLink>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('footer.residency')}
            </Typography>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
